import { AfterViewInit, Component, inject, OnDestroy, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppTopbar } from './app.topbar';
import { LayoutService, TabCloseEvent } from '@/layout/service/layout.service';
import { AppSidebar } from './app.sidebar';
import { DialogStateService } from '@/share/services/dialog-state-service';
import { CuiToastOutletComponent } from '@idealink-material/ui-core';
import { FormErrorsMessage } from '@/share/component/from-errors-mesage/form-errors-message';
import { SessionMonitorService } from '@/core/service/session-monitor.service';
import { DynamicSessionHandler } from '@/core/template/dynamic-session-handler';
import { SessionStatus } from '@/core/model/session-status';
import { STORAGE_KEYS } from '@/share/constant/storage_token.key';
import { STORAGE_SERVICE_TOKEN } from '@/core/util/storage.token';
import { DynamicDialog } from '@/share/component/dialog/ dynamic-dialog';
import { ActionMenuOverlay } from '@/share/template/button/action-menu-overlay';

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, CuiToastOutletComponent, FormErrorsMessage, DynamicSessionHandler, DynamicDialog, ActionMenuOverlay],
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
        <div class="layout-container" [ngClass]="containerClass">
            <div class="layout-blur-wrapper" [class.blurred]="dialogState.dialogOpen()">
                <div app-topbar></div>
                <div app-sidebar></div>
                <div class="layout-content-wrapper">
                    <div class="layout-content">
                        <div class="layout-content-inner">
                            <!--                        <nav app-breadcrumb></nav>-->
                            <router-outlet></router-outlet>
                            <!--                            <div app-footer></div>-->
                        </div>
                    </div>
                </div>
            </div>
            <!-- no async pipe needed, signal is read directly -->
            <app-dynamic-dialog />
        </div>
        <p-toast-outlet />
        <app-form-errors-message />
        <app-dynamic-session-handler />
        <app-action-menu-overlay />
    `
})
export class AppLayout implements AfterViewInit, OnDestroy {
    overlayMenuOpenSubscription: Subscription;

    tabOpenSubscription: Subscription;

    tabCloseSubscription: Subscription;

    menuOutsideClickListener: any;

    menuScrollListener: any;

    @ViewChild(AppSidebar) appSidebar!: AppSidebar;

    @ViewChild(AppTopbar) appTopbar!: AppTopbar;
    // @ViewChild('dialogOutlet') dialogOutlet!: DynamicDialogComponent;
    private sessionMonitorService = inject(SessionMonitorService);
    private storageService = inject(STORAGE_SERVICE_TOKEN);
    // private readonly storage = inject(STORAGE_SERVICE_TOKEN);

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        public dialogState: DialogStateService
    ) {
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    const isOutsideClicked = !(
                        (this.appSidebar.appMenu.el.nativeElement.isSameNode(event.target) || this.appSidebar.appMenu.el.nativeElement.contains(event.target))
                        // this.appTopbar.menuButton.nativeElement.isSameNode(event.target) ||
                        // this.appTopbar.menuButton.nativeElement.contains(event.target)
                    );
                    if (isOutsideClicked) {
                        this.hideMenu();
                    }
                });
            }

            if ((this.layoutService.isSlim() || this.layoutService.isSlimPlus()) && !this.menuScrollListener) {
                this.menuScrollListener = this.renderer.listen(this.appSidebar.appMenu.menuContainer.nativeElement, 'scroll', (event) => {
                    if (this.layoutService.isDesktop()) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.hideMenu();
        });

        this.tabOpenSubscription = this.layoutService.tabOpen$.subscribe((tab) => {
            this.router.navigate(tab.routerLink);
            this.layoutService.openTab(tab);
        });

        this.tabCloseSubscription = this.layoutService.tabClose$.subscribe((event: TabCloseEvent) => {
            if (
                this.router.isActive(event.tab.routerLink[0], {
                    paths: 'subset',
                    queryParams: 'subset',
                    fragment: 'ignored',
                    matrixParams: 'ignored'
                })
            ) {
                const tabs = this.layoutService.tabs;

                if (tabs.length > 1) {
                    if (event.index === tabs.length - 1) this.router.navigate(tabs[tabs.length - 2].routerLink);
                    else this.router.navigate(tabs[event.index + 1].routerLink);
                } else {
                    this.router.navigate(['/']);
                }
            }

            this.layoutService.closeTab(event.index);
        });
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({
            ...prev,
            overlayMenuActive: false,
            staticMenuMobileActive: false,
            menuHoverActive: false
        }));
        this.layoutService.reset();
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }

        if (this.menuScrollListener) {
            this.menuScrollListener();
            this.menuScrollListener = null;
        }

        this.unblockBodyScroll();
    }

    get containerClass() {
        return {
            'layout-slim': this.layoutService.layoutConfig().menuMode === 'slim',
            'layout-slim-plus': this.layoutService.layoutConfig().menuMode === 'slim-plus',
            'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
            'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
            'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
            'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive,
            'layout-static-inactive': this.layoutService.layoutState().staticMenuDesktopInactive && this.layoutService.layoutConfig().menuMode === 'static',
            'layout-light': this.layoutService.layoutConfig().layoutTheme === 'colorScheme' && !this.layoutService.isDarkTheme(),
            'layout-dark': this.layoutService.layoutConfig().layoutTheme === 'colorScheme' && this.layoutService.isDarkTheme(),
            'layout-primary': !this.layoutService.isDarkTheme() && this.layoutService.layoutConfig().layoutTheme === 'primaryColor'
        };
    }

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }

        if (this.tabOpenSubscription) {
            this.tabOpenSubscription.unsubscribe();
        }

        if (this.tabCloseSubscription) {
            this.tabCloseSubscription.unsubscribe();
        }
        this.storageService.clearAll();
    }

    ngAfterViewInit(): void {
        // this.dialogMgr.register(this.dialogOutlet);
        const status = this.storageService.get<SessionStatus>(STORAGE_KEYS.SESSION_MONITOR) || SessionStatus.Active;
        this.sessionMonitorService.setStatus(status);
    }
}
